import { Briefcase, Users } from "lucide-react";
import { useInput, useTranslate } from "ra-core";
import { useFormContext } from "react-hook-form";
import { useId } from "react";

import { useIsMobile } from "@/hooks/use-mobile";

import { SegmentedControl, type SegmentedOption } from "./SegmentedControl";
import { getTaskKind, type TaskKind } from "./taskKind";

/**
 * "Interna / Proyecto / Cliente" toggle of the task form. Switching to
 * Interna drops the contact link (the database rejects an internal task with
 * a contact); switching back leaves the contact empty, to be picked.
 */
export const TaskKindInput = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { setValue, getValues } = useFormContext();
  const { field } = useInput({ source: "kind" });
  const hintId = useId();
  const kind = getTaskKind({
    kind: field.value,
    contact_id: getValues("contact_id"),
  });

  const options: SegmentedOption<TaskKind>[] = [
    {
      value: "internal",
      label: translate("resources.tasks.kind.internal"),
      icon: <Users aria-hidden />,
    },
    {
      value: "client",
      label: translate("resources.tasks.kind.client"),
      icon: <Briefcase aria-hidden />,
    },
  ];

  const onChange = (next: TaskKind) => {
    if (next === kind) return;
    field.onChange(next);
    if (next === "internal") {
      setValue("contact_id", null, { shouldDirty: true });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span aria-hidden className="text-sm font-medium">
        {translate("resources.tasks.kind.label")}
      </span>
      <SegmentedControl
        value={kind}
        options={options}
        onChange={onChange}
        label={translate("resources.tasks.kind.label")}
        aria-describedby={hintId}
        glass={isMobile}
        className="w-full"
      />
      <p id={hintId} className="text-xs text-muted-foreground">
        {translate(`resources.tasks.kind.${kind}_hint`)}
      </p>
    </div>
  );
};
